import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import css from './RacingThemeShopPurchasePanel.module.css';

const LockIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const CartIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

/**
 * Shop listing purchase / CTA column.
 */
const RacingThemeShopPurchasePanel = props => {
  const {
    className,
    title,
    price,
    blurb,
    conditionOptions,
    sizeOptions,
    addonOptions,
    sizeGuideHref,
    primaryCta,
    secondaryCta,
    trustText,
    onAddToCart,
    onBuyNow,
  } = props;

  const [condition, setCondition] = useState(conditionOptions[0] || '');
  const [size, setSize] = useState(sizeOptions[0] || '');
  const [addon, setAddon] = useState(addonOptions[0] || '');
  const [qty, setQty] = useState(1);

  return (
    <aside className={classNames(css.root, className)}>
      <h2 className={css.title}>{title}</h2>
      <p className={css.price}>{price}</p>
      {blurb ? <p className={css.blurb}>{blurb}</p> : null}

      <label className={css.field}>
        <span className={css.fieldLabel}>Condition</span>
        <select
          className={css.select}
          value={condition}
          onChange={event => setCondition(event.target.value)}
        >
          {conditionOptions.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>

      <label className={css.field}>
        <span className={css.fieldLabelRow}>
          <span className={css.fieldLabel}>Size</span>
          {sizeGuideHref ? (
            <a className={css.sizeGuide} href={sizeGuideHref}>
              Size Guide
            </a>
          ) : null}
        </span>
        <select className={css.select} value={size} onChange={event => setSize(event.target.value)}>
          {sizeOptions.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>

      <label className={css.field}>
        <span className={css.fieldLabel}>Add-on Embroidery</span>
        <select
          className={css.select}
          value={addon}
          onChange={event => setAddon(event.target.value)}
        >
          {addonOptions.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>

      <div className={css.field}>
        <span className={css.fieldLabel}>Quantity</span>
        <div className={css.stepper}>
          <button
            type="button"
            className={css.stepBtn}
            aria-label="Decrease quantity"
            onClick={() => setQty(value => Math.max(1, value - 1))}
          >
            −
          </button>
          <span className={css.qty} aria-live="polite">
            {qty}
          </span>
          <button
            type="button"
            className={css.stepBtn}
            aria-label="Increase quantity"
            onClick={() => setQty(value => value + 1)}
          >
            +
          </button>
        </div>
      </div>

      <button className={css.primaryBtn} type="button" onClick={onAddToCart}>
        <CartIcon />
        {primaryCta}
      </button>
      <button className={css.secondaryBtn} type="button" onClick={onBuyNow}>
        {secondaryCta}
      </button>

      <p className={css.trust}>
        <LockIcon />
        {trustText}
      </p>
    </aside>
  );
};

RacingThemeShopPurchasePanel.defaultProps = {
  className: null,
  blurb: null,
  sizeGuideHref: null,
  conditionOptions: [],
  sizeOptions: [],
  addonOptions: [],
  primaryCta: 'Add to Cart',
  secondaryCta: 'Buy It Now',
  trustText: 'Secure checkout',
  onAddToCart: () => {},
  onBuyNow: () => {},
};

RacingThemeShopPurchasePanel.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string.isRequired,
  price: PropTypes.string.isRequired,
  blurb: PropTypes.string,
  conditionOptions: PropTypes.arrayOf(PropTypes.string),
  sizeOptions: PropTypes.arrayOf(PropTypes.string),
  addonOptions: PropTypes.arrayOf(PropTypes.string),
  sizeGuideHref: PropTypes.string,
  primaryCta: PropTypes.string,
  secondaryCta: PropTypes.string,
  trustText: PropTypes.string,
  onAddToCart: PropTypes.func,
  onBuyNow: PropTypes.func,
};

export default RacingThemeShopPurchasePanel;
